import * as Sentry from "@sentry/react";
import {
    scrubSentryBreadcrumb,
    scrubSentryEventBreadcrumbs
} from "@taskhealth/shared/sentry-data-collection";
import Constants from "expo-constants";

export type SentryBreadcrumbLevel = "error" | "warning" | "info";

export function initSentry(): boolean {
    const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;
    if (!dsn) {
        return false;
    }
    Sentry.init({
        dsn,
        environment: __DEV__ ? "development" : "production",
        release: `taskhealth-mobile-web@${Constants.expoConfig?.version ?? "unknown"}`,
        tracesSampleRate: __DEV__ ? 1.0 : 0.2,
        attachStacktrace: true,
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === "navigation" && breadcrumb.level === "info") {
                return null;
            }
            // No console lines, no URL query values (names, search, keys): see
            // packages/shared/src/sentry-data-collection.ts.
            return scrubSentryBreadcrumb(breadcrumb);
        },
        // Native network breadcrumbs are merged into the event without
        // beforeBreadcrumb; scrub them here too.
        beforeSend(event) {
            return scrubSentryEventBreadcrumbs(event);
        }
    });
    return true;
}

export function setSentryUser(id: string | null) {
    Sentry.setUser(id ? { id } : null);
}

export function addSentryBreadcrumb(params: {
    category: string;
    level: SentryBreadcrumbLevel;
    message: string;
}) {
    Sentry.addBreadcrumb(params);
}

export function captureSentryMessage(message: string, level: SentryBreadcrumbLevel) {
    Sentry.captureMessage(message, level);
}

export function captureSentryEvent(params: {
    message: string;
    level?: SentryBreadcrumbLevel;
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
}) {
    Sentry.captureMessage(params.message, {
        level: params.level ?? "error",
        tags: params.tags,
        extra: params.extra
    });
}

export function captureSentryException(error: unknown) {
    Sentry.captureException(error);
}

export function triggerSentryNativeCrash() {
    // No native crash path on web — surface as a caught error instead.
    Sentry.captureException(new Error("[sentry-test] nativeCrash called on web (no-op)"));
}

export function wrapRoot<T extends React.ComponentType<unknown>>(component: T): T {
    // @sentry/react has no wrap() equivalent; consumers can use <Sentry.ErrorBoundary>
    // directly if they want a render error boundary on web.
    return component;
}
